import isNull from "lodash/isNull";
import { bugNotif } from "../../api/bugsnag";
import { getSubscription } from "../subscription";
import { ViewWLSubscriptionsTiers } from "../../../Common/models/subscriptions";
import { ttqTrackEvent } from "../../api/tiktokEvents";
import { getStripePriceValueWithPromo } from "../promo";
import { getUserHashedDetails } from "../../api/user";
import { getErrorMsg, hashStringToSHA256 } from "../../../Common/utils/common";
import Stripe from "stripe";

declare const ttq: {
  identify: (data: { email?: string; external_id?: string; phone_number?: string }) => void;
  track: (event: string, data: unknown, options?: { event_id?: string }) => void;
};

export const sendttqTracking = async (
  userId: string,
  pid: string,
  value: number
) => {
  const event_id = Math.random().toString(36).substring(2, 17);
  try {
    const {
      data: {
        result: { emailHashed, phoneNumberHashed, hashedUserId },
      },
    } = await getUserHashedDetails(userId);

    ttq.identify({
      email: emailHashed,
      external_id: hashedUserId,
      phone_number: phoneNumberHashed,
    });

    const subscription = await getSubscription(pid);
    if (!isNull(subscription)) {
      ttq.track(
        "CompletePayment",
        {
          contents: [
            {
              content_id: subscription.docId,
              content_type: "product",
              content_name: subscription.name,
            },
          ],
          value,
          currency: "AUD",
        },
        { event_id }
      );
    }
  } catch (error) {
    console.log("error sendttqTracking", error);
    bugNotif("sendttqTracking", getErrorMsg(error).rawErrorMsg);
  }
  return event_id;
};

export const sendttqAddToCart = async (
  emailAddress: string,
  pid: string,
  value: number
) => {
  const event_id = Math.random().toString(36).substring(2, 17);
  try {
    const hashedEmail = await hashStringToSHA256(emailAddress);

    ttq.identify({
      email: hashedEmail,
    });

    const subscription = await getSubscription(pid);
    if (!isNull(subscription)) {
      ttq.track(
        "AddToCart",
        {
          contents: [
            {
              content_id: subscription.docId,
              content_type: "product",
              content_name: subscription.name,
            },
          ],
          value,
          currency: "AUD",
        },
        { event_id }
      );
    }
  } catch (error) {
    console.log("error sendttqAddToCart", error);
    bugNotif("sendttqAddToCart", getErrorMsg(error).rawErrorMsg);
  }

  return event_id;
};

export const sendTTQViewEvent = async (
  emailAddress: string,
  subscription: ViewWLSubscriptionsTiers
) => {
  try {
    const hashedEmail = await hashStringToSHA256(emailAddress);

    const tiktokSource = new URLSearchParams(window.location.search).get(
      "ttclid"
    );
    await ttqTrackEvent({
      event: "ViewContent",
      user: {
        email: hashedEmail,
        ...(!isNull(tiktokSource)
          ? {
              ttclid: tiktokSource,
            }
          : {}),
      },
      product: {
        id: subscription.docId,
        name: subscription.name,
      },
      url: location.href,
    });
  } catch (error) {
    bugNotif("sendTTQViewEvent", getErrorMsg(error).rawErrorMsg);
  }
};

export const sendTTQCheckoutEvent = async (
  emailAddress: string,
  phoneNumber: string,
  subscription: ViewWLSubscriptionsTiers,
  tiktokSource: string | null,
  validStripeCoupon: undefined | null | Stripe.Coupon
) => {
  try {
    const hashedEmail = await hashStringToSHA256(emailAddress);
    const hashedPhone = await hashStringToSHA256(phoneNumber);

    const value =
      validStripeCoupon !== undefined && !isNull(validStripeCoupon)
        ? getStripePriceValueWithPromo(subscription.price, validStripeCoupon)
        : subscription.price;

    await Promise.all([
      ttqTrackEvent({
        event: "AddPaymentInfo",
        user: {
          email: hashedEmail,
          phone: hashedPhone,
          ...(!isNull(tiktokSource)
            ? {
                ttclid: tiktokSource,
              }
            : {}),
        },
        product: {
          id: subscription.docId,
          name: subscription.name,
          value,
        },
        url: location.href,
      }),

      ttqTrackEvent({
        event: "InitiateCheckout",
        user: {
          email: hashedEmail,
          phone: hashedPhone,
          ...(!isNull(tiktokSource)
            ? {
                ttclid: tiktokSource,
              }
            : {}),
        },
        product: {
          id: subscription.docId,
          name: subscription.name,
          value,
        },
        url: location.href,
      }),
    ]);
  } catch (error) {
    bugNotif("sendTTQCheckoutEvent", getErrorMsg(error).rawErrorMsg);
  }
};

export const sendTTQCompletedPurchaseEvent = async (
  userId: string,
  value: number,
  subscriptionId: string,
  tiktokSource: string | null,
  completePaymentEventId: string
) => {
  try {
    const {
      data: {
        result: { emailHashed, phoneNumberHashed },
      },
    } = await getUserHashedDetails(userId);

    const subscription = await getSubscription(subscriptionId);
    if (isNull(subscription)) {
      return;
    }
    await Promise.all([
      ttqTrackEvent({
        event: "CompletePayment",
        event_id: completePaymentEventId,
        user: {
          email: emailHashed,
          phone: phoneNumberHashed,
          ...(!isNull(tiktokSource)
            ? {
                ttclid: tiktokSource,
              }
            : {}),
        },
        product: {
          id: subscription.docId,
          name: subscription.name,
          value,
        },
        url: location.href,
      }),

      ttqTrackEvent({
        event: "CompleteRegistration",
        user: {
          email: emailHashed,
          phone: phoneNumberHashed,
          ...(!isNull(tiktokSource)
            ? {
                ttclid: tiktokSource,
              }
            : {}),
        },
        product: {
          id: subscription.docId,
          name: subscription.name,
          value,
        },
        url: location.href,
      }),
    ]);
  } catch (error) {
    bugNotif("sendTTQCompletedPurchaseEvent", getErrorMsg(error).rawErrorMsg);
  }
};

